import { useCallback } from 'react';
import { AiPromptType } from '@core/generated/gql-types';
import { useAiStore } from '@core/ai/useAiStore';

export type AiJobDescriptionSiteAddress = {
  suburb?: string | null;
  state?: string | null;
};

export type AiJobDescriptionInput = {
  classificationName?: string | null;
  startTime?: string | null;
  clientName?: string | null;
  siteAddress?: AiJobDescriptionSiteAddress | null;
  /** Fallback when suburb/state are unavailable (e.g. site name). */
  siteLabel?: string | null;
};

type UseGenerateAiJobDescriptionParams = {
  /** Read latest form/job values when Generate is pressed. */
  getInput: () => AiJobDescriptionInput;
  /** Formik field that receives the generated text / validation errors. */
  descriptionField: string;
  setFieldError: (field: string, message: string | undefined) => void;
  setFieldValue: (
    field: string,
    value: string,
    shouldValidate?: boolean,
  ) => unknown;
};

const formatSiteAddress = (
  siteAddress?: AiJobDescriptionSiteAddress | null,
  siteLabel?: string | null,
): string => {
  if (siteAddress) {
    return `${siteAddress.suburb ?? ''}, ${siteAddress.state ?? ''}`.replace(
      /^,\s*|,\s*$/g,
      '',
    );
  }
  return siteLabel ?? '';
};

/**
 * Shared "Generate with AI" flow for job create and edit description fields.
 */
export const useGenerateAiJobDescription = ({
  getInput,
  descriptionField,
  setFieldError,
  setFieldValue,
}: UseGenerateAiJobDescriptionParams) => {
  const { fetchAiResponse, isLoading: isGeneratingAi } = useAiStore();

  const generateAiJobDescription = useCallback(async () => {
    const {
      classificationName,
      startTime,
      clientName,
      siteAddress,
      siteLabel,
    } = getInput();

    const missingFields: string[] = [];
    if (!classificationName) missingFields.push('classification');
    if (!startTime) missingFields.push('start time');
    if (!clientName) missingFields.push('client');
    if (!siteAddress && !siteLabel) missingFields.push('site');

    if (missingFields.length > 0) {
      setFieldError(
        descriptionField,
        `Please fill in the following fields first: ${missingFields.join(', ')}`,
      );
      return;
    }

    const jobDescription = await fetchAiResponse(
      AiPromptType.SuggestJobDescription,
      {
        classification: classificationName as string,
        startTime: startTime as string,
        clientName: clientName as string,
        address: formatSiteAddress(siteAddress, siteLabel),
      },
    );

    if (jobDescription) {
      await setFieldValue(descriptionField, jobDescription);
    }
  }, [
    getInput,
    descriptionField,
    setFieldError,
    setFieldValue,
    fetchAiResponse,
  ]);

  return {
    generateAiJobDescription,
    isGeneratingAi,
  };
};

export default useGenerateAiJobDescription;
